import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Toolbar,
  Box,
  LinearProgress,
  Slider,
  Collapse,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  Replay10 as Replay10Icon,
  Forward10 as Forward10Icon,
  VideoFile as VideoFileIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Subtitles as SubtitlesIcon,
  PictureInPicture as PictureInPictureIcon,
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useWebSocket } from '../WebSocketContext';

import { getEventSummary } from '../../Utils/timeEvent';

// Theme colors
const primaryColor = '#5757b0';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    maxWidth: '95vw',
    maxHeight: '95vh',
    width: '80vw',
    height: '80vh',
    backgroundColor: '#000',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 8px 32px rgba(87, 87, 176, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  '& .MuiDialog-paper.MuiDialog-paperFullScreen': {
    width: '100vw',
    height: '100vh',
    maxWidth: '100vw',
    maxHeight: '100vh',
    borderRadius: 0,
    margin: 0,
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiDialog-paper': {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      borderRadius: 0,
      margin: 0,
    },
  },
  '& .MuiBackdrop-root': {
    backdropFilter: 'blur(8px)',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: `linear-gradient(135deg, ${primaryColor} 0%, #6b6bce 50%, #8080d7 100%)`,
  color: 'white',
  padding: '12px 20px',
  paddingTop: 'calc(12px + env(safe-area-inset-top))',
  paddingLeft: 'calc(20px + env(safe-area-inset-left))',
  paddingRight: 'calc(20px + env(safe-area-inset-right))',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontWeight: 700,
  fontSize: '1.1rem',
  position: 'relative',
  boxShadow: '0 2px 8px rgba(87, 87, 176, 0.25)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
  '& .MuiTypography-root': {
    flex: 1,
    margin: 0,
    fontWeight: 600,
    fontSize: '1.1rem',
    letterSpacing: '0.025em',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '10px 12px',
    paddingTop: 'calc(10px + env(safe-area-inset-top))',
    paddingLeft: 'calc(12px + env(safe-area-inset-left))',
    paddingRight: 'calc(12px + env(safe-area-inset-right))',
    fontSize: '1rem',
    flexWrap: 'wrap',
    gap: '8px',
    '& .MuiTypography-root': {
      fontSize: '1rem',
      minWidth: 0,
      wordBreak: 'break-word',
    },
  },
}));

const ToolbarContainer = styled(Toolbar)(({ theme }) => ({
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  minHeight: '48px !important',
  padding: '0 20px',
  justifyContent: 'space-between',
  backdropFilter: 'blur(10px)',
  color: 'white',
  [theme.breakpoints.down('sm')]: {
    padding: '0 12px',
    minHeight: '44px !important',
    flexWrap: 'wrap',
    gap: '8px',
    justifyContent: 'flex-start',
  },
}));

const VideoContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: '#000',
  position: 'relative',
}));

const VideoWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& video': {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
}));

const VideoControls = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  padding: '10px 20px',
  transition: 'opacity 0.3s ease',
  backdropFilter: 'blur(5px)',
}));

const ControlsRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '8px',
}));

const PlaybackControls = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}));

const TimeDisplay = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '14px',
  color: 'white',
  minWidth: '100px',
}));

const VolumeContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  minWidth: '120px',
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  flexDirection: 'column',
  gap: '16px',
  color: 'white',
}));

const TranscriptionContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: '80px',
  left: '20px',
  right: '20px',
  maxHeight: '200px',
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  borderRadius: '8px',
  padding: '12px',
  backdropFilter: 'blur(10px)',
  color: 'white',
  overflow: 'auto',
  fontSize: '14px',
  lineHeight: '1.4',
}));

const VideoViewer = React.forwardRef(({ open, onClose, filename = '', eventId = null, timeevent = null }, ref) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [videoBlob, setVideoBlob] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [totalChunks, setTotalChunks] = useState(0);
  const [controlsVisible, setControlsVisible] = useState(true);
  
  // Video playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [previousVolume, setPreviousVolume] = useState(1);
  const [buffered, setBuffered] = useState(0);
  
  // Transcription state
  const [transcription, setTranscription] = useState('');
  const [transcriptionVisible, setTranscriptionVisible] = useState(false);
  const [transcriptionLoading, setTranscriptionLoading] = useState(false);
  const [hasTranscription, setHasTranscription] = useState(false);
  
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const chunksRef = useRef([]);
  const controlsTimeoutRef = useRef(null);
  const { subscribeToMessages } = useWebSocket();

  const matchesMessage = useCallback((data = {}) => {
    const msgName = (data.filename || data.name || '').toLowerCase();
    const msgPath = (data.path || '').toLowerCase();
    const fn = (filename || '').toLowerCase();

    if (eventId && data.event_id === eventId) return true;

    if (!eventId) {
      if (fn) {
        if (msgName && msgName === fn) return true;
        if (msgPath && msgPath.endsWith(fn)) return true;
      }
      if (!msgName && !msgPath) return true;
    }

    return false;
  }, [eventId, filename]);

  // Auto-hide controls
  const resetControlsTimeout = useCallback(() => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    setControlsVisible(true);
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) {
        setControlsVisible(false);
      }
    }, 3000);
  }, [isPlaying]);

  const handleMouseMove = useCallback(() => {
    resetControlsTimeout();
  }, [resetControlsTimeout]);

  // Request transcription for the video file
  const requestTranscription = useCallback(() => {
    if (!eventId && !timeevent) return;
    
    setTranscriptionLoading(true);
    
    // If we have timeevent data, use it directly
    if (timeevent && timeevent.eventdata) {
      setTranscriptionLoading(false);
      setHasTranscription(true);
      
      // Extract transcription from timeevent.eventdata
      let transcriptionText = '';
      
      if (timeevent.eventdata.transcription) {
        transcriptionText = timeevent.eventdata.transcription;
      } else if (timeevent.eventdata.text) {
        transcriptionText = timeevent.eventdata.text;
      } else if (timeevent.eventdata.content) {
        transcriptionText = timeevent.eventdata.content;
      } else if (timeevent.eventdata.description) {
        transcriptionText = timeevent.eventdata.description;
      } else {
        const summary = getEventSummary(timeevent);
        if (summary) {
          transcriptionText = `Event Summary:\n\n${summary}`;
        } else if (timeevent.eventdata) {
          transcriptionText = `Event Data:\n\n${timeevent.eventdata}`;
        } else {
          transcriptionText = t('viewer.no_transcription');
        }
      }
      
      setTranscription(transcriptionText);
      return;
    }
    
    // Fallback
    setTimeout(() => {
      setTranscriptionLoading(false);
      setHasTranscription(true);
      setTranscription(t('viewer.no_transcription'));
    }, 1000);
  }, [eventId, timeevent, t]);

  // Format time helper function
  const formatTime = useCallback((time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  // Cleanup blob URL when component unmounts or dialog closes
  useEffect(() => {
    return () => {
      if (videoUrl) {
        URL.revokeObjectURL(videoUrl);
      }
    };
  }, [videoUrl]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setVideoBlob(null);
      setLoading(false);
      setProgress(0);
      setError(null);
      setTotalChunks(0);
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      setTranscription('');
      setTranscriptionVisible(false);
      chunksRef.current = [];
    } else {
      setVideoBlob(null);
      setLoading(true);
      setProgress(0);
      setError(null);
      setTotalChunks(0);
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      setTranscription('');
      setTranscriptionVisible(false);
      chunksRef.current = [];
      
      // Request transcription if we have an eventId or timeevent data
      if (eventId || timeevent) {
        requestTranscription();
      }
    }
  }, [open, eventId, timeevent, requestTranscription]);

  // Handle videoUrl cleanup separately
  useEffect(() => {
    if (!open && videoUrl) {
      URL.revokeObjectURL(videoUrl);
      setVideoUrl(null);
    }
  }, [open, videoUrl]);

  const assembleVideo = useCallback((chunks) => {
    try {
      let totalLength = 0;
      chunks.forEach(chunk => {
        totalLength += chunk.length;
      });

      const combined = new Uint8Array(totalLength);
      let offset = 0;
      chunks.forEach(chunk => {
        combined.set(chunk, offset);
        offset += chunk.length;
      });

      // Determine MIME type based on file extension
      const extension = filename.toLowerCase().split('.').pop();
      let mimeType = 'video/mp4'; // default
      
      switch (extension) {
        case 'mp4':
          mimeType = 'video/mp4';
          break;
        case 'webm':
          mimeType = 'video/webm';
          break;
        case 'avi':
          mimeType = 'video/x-msvideo';
          break;
        case 'mov':
          mimeType = 'video/quicktime';
          break;
        case 'mkv':
          mimeType = 'video/x-matroska';
          break;
        default:
          mimeType = 'video/mp4';
      }

      const blob = new Blob([combined], { type: mimeType });
      const url = URL.createObjectURL(blob);
      
      setVideoBlob(blob);
      setVideoUrl(url);
      setLoading(false);
    } catch (error) {
      console.error('Error assembling video:', error);
      setError('Failed to assemble video file');
      setLoading(false);
    }
  }, [filename]);

  // Handle video stream messages
  const handleVideoStreamMessage = useCallback((message) => {
    switch (message.status) {
      case 'video_stream_start':
        if (matchesMessage(message)) {
          setLoading(true);
          setProgress(0);
          setError(null);
          setTotalChunks(message.total_chunks || 0);
          chunksRef.current = [];
        }
        break;

      case 'video_stream_chunk':
        if (matchesMessage(message)) {
          try {
            const binaryString = atob(message.chunk_data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }

            const chunkIndex = message.chunk_number;
            if (!chunksRef.current[chunkIndex]) {
              chunksRef.current[chunkIndex] = bytes;
              
              const receivedChunks = chunksRef.current.filter(chunk => chunk).length;
              if (totalChunks > 0) {
                setProgress((receivedChunks / totalChunks) * 100);
              }
            }

            if (message.is_last_chunk) {
              setTimeout(() => {
                const validChunks = chunksRef.current.filter(chunk => chunk);
                if (validChunks.length > 0) {
                  assembleVideo(validChunks);
                } else {
                  setError('Failed to assemble video - no chunks received');
                  setLoading(false);
                }
              }, 100);
            }
          } catch (error) {
            console.error('Error processing video chunk:', error);
            setError('Failed to process video chunk');
          }
        }
        break;

      case 'video_stream_complete':
        if (matchesMessage(message)) {
          if (!videoUrl) {
            const validChunks = chunksRef.current.filter(chunk => chunk);
            if (validChunks.length > 0) {
              assembleVideo(validChunks);
            }
          }
        }
        break;

      case 'error':
        if (matchesMessage(message)) {
          setError(message.message || 'Failed to load video');
          setLoading(false);
        }
        break;

      default:
        break;
    }
  }, [matchesMessage, totalChunks, videoUrl, assembleVideo]);

  useEffect(() => {
    if (!open) return;

    const unsubscribe = subscribeToMessages(['openfile'], (parsedMessage) => {
      if (parsedMessage.data &&
          ['video_stream_start', 'video_stream_chunk', 'video_stream_complete', 'error'].includes(parsedMessage.data.status) &&
          matchesMessage(parsedMessage.data)) {
        handleVideoStreamMessage(parsedMessage.data);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [open, subscribeToMessages, handleVideoStreamMessage, matchesMessage]);

  // Timeout handler
  useEffect(() => {
    if (!open || !loading || videoUrl || error) return;

    const timeout = setTimeout(() => {
      setError('Timeout waiting for video content. The file might not be a valid video file or there might be a server issue.');
      setLoading(false);
    }, 30000); // 30 second timeout for video

    return () => {
      clearTimeout(timeout);
    };
  }, [open, loading, videoUrl, error, eventId]);

  // Video event handlers
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    const handleError = () => {
      setError('Failed to load video file');
      setLoading(false);
    };

    const handleProgress = () => {
      if (video.buffered.length > 0) {
        const bufferedEnd = video.buffered.end(video.buffered.length - 1);
        const bufferedPercent = (bufferedEnd / video.duration) * 100;
        setBuffered(bufferedPercent);
      }
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('error', handleError);
    video.addEventListener('progress', handleProgress);

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('error', handleError);
      video.removeEventListener('progress', handleProgress);
    };
  }, [videoUrl]);

  // Controls visibility
  useEffect(() => {
    resetControlsTimeout();
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [resetControlsTimeout]);

  const downloadVideo = () => {
    if (videoBlob) {
      const link = document.createElement('a');
      link.href = videoUrl;
      link.download = filename || 'video';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (containerRef.current && containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  const togglePictureInPicture = async () => {
    const video = videoRef.current;
    if (!video) return;

    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else {
        await video.requestPictureInPicture();
      }
    } catch (error) {
      console.error('Picture-in-Picture error:', error);
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Video control functions
  const togglePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
    } else {
      video.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (event, newValue) => {
    const video = videoRef.current;
    if (!video) return;

    const seekTime = (newValue / 100) * duration;
    video.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  const handleVolumeChange = (event, newValue) => {
    const video = videoRef.current;
    if (!video) return;

    const newVolume = newValue / 100;
    video.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isMuted) {
      video.volume = previousVolume;
      setVolume(previousVolume);
      setIsMuted(false);
    } else {
      setPreviousVolume(volume);
      video.volume = 0;
      setVolume(0);
      setIsMuted(true);
    }
  };

  const skipBackward = () => {
    const video = videoRef.current;
    if (!video) return;

    video.currentTime = Math.max(0, video.currentTime - 10);
  };

  const skipForward = () => {
    const video = videoRef.current;
    if (!video) return;

    video.currentTime = Math.min(duration, video.currentTime + 10);
  };

  const toggleTranscription = () => {
    setTranscriptionVisible(!transcriptionVisible);
  };

  return (
    <StyledDialog 
      open={open} 
      onClose={onClose}
      maxWidth={false}
      fullScreen={isFullscreen || isSmallScreen}
    >
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <VideoFileIcon sx={{ 
            mr: 1.5, 
            fontSize: '1.4rem',
            filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2))'
          }} />
          <Typography variant="h6" component="div" sx={{ margin: 0 }}>
            {filename ? `${t('viewer.video_player_title')} - ${filename}` : t('viewer.video_player_title')}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ 
            color: 'white',
            marginLeft: 'auto',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(4px)',
            borderRadius: '8px',
            width: '36px',
            height: '36px',
            transition: 'all 0.3s ease',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              transform: 'scale(1.05)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              borderColor: 'rgba(255, 255, 255, 0.4)',
            },
          }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <ToolbarContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton 
            onClick={downloadVideo} 
            disabled={!videoBlob}
            size="small"
            title={t('viewer.download_video')}
            sx={{ color: 'white' }}
          >
            <DownloadIcon />
          </IconButton>
          <IconButton 
            onClick={togglePictureInPicture}
            disabled={!videoUrl}
            size="small"
            title="Picture in Picture"
            sx={{ color: 'white' }}
          >
            <PictureInPictureIcon />
          </IconButton>
          <IconButton 
            onClick={toggleFullscreen}
            size="small"
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
            sx={{ color: 'white' }}
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Box>
        
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, mx: 2 }}>
            <Typography variant="body2" color="white">
              {t('viewer.loading_video')} {Math.round(progress)}%
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ flex: 1, maxWidth: 200 }}
            />
          </Box>
        )}
        
        {hasTranscription && (
          <IconButton 
            onClick={toggleTranscription}
            size="small"
            title={transcriptionVisible ? "Hide Transcription" : "Show Transcription"}
            sx={{ 
              color: transcriptionVisible ? primaryColor : 'white',
              backgroundColor: transcriptionVisible ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
            }}
          >
            <SubtitlesIcon />
          </IconButton>
        )}
      </ToolbarContainer>

      <DialogContent sx={{ padding: 0, height: '100%', overflow: 'hidden' }}>
        <VideoContainer
          ref={containerRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setControlsVisible(false)}
        >
          {loading ? (
            <LoadingContainer>
              <LinearProgress 
                variant="determinate" 
                value={progress} 
                sx={{ width: 400, mb: 2 }}
              />
              <Typography color="white">
                {t('viewer.loading_video')} {Math.round(progress)}%
              </Typography>
            </LoadingContainer>
          ) : error ? (
            <LoadingContainer>
              <Typography color="error" variant="h6" gutterBottom>
                {t('viewer.error_loading_video')}
              </Typography>
              <Typography color="white">
                {error}
              </Typography>
            </LoadingContainer>
          ) : videoUrl ? (
            <VideoWrapper>
              <video 
                ref={videoRef} 
                src={videoUrl} 
                preload="metadata"
                controls={false}
                onClick={togglePlayPause}
              />
              
              {/* Transcription overlay */}
              {transcriptionVisible && hasTranscription && transcription && (
                <TranscriptionContainer>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {transcription}
                  </Typography>
                </TranscriptionContainer>
              )}
              
              {/* Custom video controls */}
              <Collapse in={controlsVisible}>
                <VideoControls>
                  {/* Progress bar */}
                  <Box sx={{ mb: 1 }}>
                    <Slider
                      value={duration > 0 ? (currentTime / duration) * 100 : 0}
                      onChange={handleSeek}
                      sx={{
                        color: 'white',
                        '& .MuiSlider-thumb': {
                          width: 16,
                          height: 16,
                        },
                        '& .MuiSlider-track': {
                          backgroundColor: 'white',
                        },
                        '& .MuiSlider-rail': {
                          backgroundColor: 'rgba(255, 255, 255, 0.3)',
                        },
                      }}
                    />
                  </Box>
                  
                  <ControlsRow>
                    {/* Playback controls */}
                    <PlaybackControls>
                      <IconButton onClick={skipBackward} disabled={!videoUrl} sx={{ color: 'white' }}>
                        <Replay10Icon />
                      </IconButton>
                      
                      <IconButton 
                        onClick={togglePlayPause} 
                        disabled={!videoUrl}
                        sx={{
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          width: 48,
                          height: 48,
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.3)',
                          },
                          '&:disabled': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            color: 'rgba(255, 255, 255, 0.5)',
                          },
                        }}
                      >
                        {isPlaying ? <PauseIcon sx={{ fontSize: 28 }} /> : <PlayIcon sx={{ fontSize: 28 }} />}
                      </IconButton>
                      
                      <IconButton onClick={skipForward} disabled={!videoUrl} sx={{ color: 'white' }}>
                        <Forward10Icon />
                      </IconButton>
                    </PlaybackControls>

                    {/* Time display */}
                    <TimeDisplay>
                      <Typography variant="body2" sx={{ color: 'white' }}>
                        {formatTime(currentTime)} / {formatTime(duration)}
                      </Typography>
                    </TimeDisplay>

                    {/* Volume control */}
                    <VolumeContainer>
                      <IconButton onClick={toggleMute} size="small" sx={{ color: 'white' }}>
                        {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                      </IconButton>
                      <Slider
                        value={volume * 100}
                        onChange={handleVolumeChange}
                        sx={{
                          color: 'white',
                          width: 80,
                          '& .MuiSlider-thumb': {
                            width: 12,
                            height: 12,
                          },
                        }}
                      />
                    </VolumeContainer>
                  </ControlsRow>
                </VideoControls>
              </Collapse>
            </VideoWrapper>
          ) : (
            <LoadingContainer>
              <Typography color="white">
                No video content available
              </Typography>
            </LoadingContainer>
          )}
        </VideoContainer>
      </DialogContent>
    </StyledDialog>
  );
});

VideoViewer.displayName = 'VideoViewer';

export default VideoViewer;
